import { useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { Button } from './Button'
import { Logo } from './Logo'
import { CardIcon, CloseIcon, DocumentIcon, DownloadIcon, HomeIcon, MenuIcon, UserIcon } from './icons'

const navItems = [
  { to: '/', label: 'Home', icon: HomeIcon, end: true },
  { to: '/downloads', label: 'Downloads', icon: DownloadIcon },
  { to: '/agreements', label: 'Agreements', icon: DocumentIcon },
  { to: '/billing', label: 'Billing', icon: CardIcon },
  { to: '/account', label: 'Account', icon: UserIcon },
]

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav aria-label="Portal sections" className="flex flex-col gap-1">
      {navItems.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex min-h-[44px] items-center gap-3 rounded-xl px-4 text-small font-medium transition-colors duration-micro ease-porcelain ${
              isActive
                ? 'bg-ground-3 text-cobalt'
                : 'text-ink-muted hover:bg-ground-3 hover:text-ink'
            }`
          }
        >
          <Icon />
          {label}
        </NavLink>
      ))}
    </nav>
  )
}

function RailFooter({ compact = false }: { compact?: boolean }) {
  const { user, memberships, signOut } = useAuth()
  const orgName = memberships[0]?.organizations?.name

  return (
    <div className={`flex flex-col gap-3 border-t border-line ${compact ? 'p-4' : 'p-4'}`}>
      <div className="min-w-0">
        {orgName && <p className="truncate text-small font-medium text-ink">{orgName}</p>}
        <p className="truncate text-small text-ink-muted">{user?.email ?? 'Signed in'}</p>
      </div>
      <Button variant="secondary" size="sm" onClick={() => void signOut()}>
        Sign out
      </Button>
    </div>
  )
}

/**
 * Authenticated app shell: persistent left rail on desktop, top bar + drawer
 * on mobile. Every page renders into the <Outlet />.
 */
export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const location = useLocation()

  return (
    <div className="min-h-screen bg-ground">
      {/* Desktop rail */}
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-64 flex-col border-r border-line bg-ground-2 lg:flex">
        <div className="p-6">
          <Logo />
        </div>
        <div className="flex-1 px-3">
          <NavItems />
        </div>
        <RailFooter />
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-line bg-ground-2 px-4 py-3 lg:hidden">
        <Logo />
        <button
          type="button"
          className="flex h-11 w-11 items-center justify-center rounded-xl text-ink hover:bg-ground-3"
          aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen((open) => !open)}
        >
          {drawerOpen ? <CloseIcon /> : <MenuIcon />}
        </button>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-30 lg:hidden">
          <div
            className="absolute inset-0 bg-ink/40"
            aria-hidden="true"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="absolute inset-y-0 right-0 flex w-72 max-w-[85vw] flex-col bg-ground-2 shadow-xl">
            <div className="flex items-center justify-between p-4">
              <Logo />
              <button
                type="button"
                className="flex h-11 w-11 items-center justify-center rounded-xl text-ink hover:bg-ground-3"
                aria-label="Close menu"
                onClick={() => setDrawerOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className="flex-1 px-3">
              <NavItems onNavigate={() => setDrawerOpen(false)} />
            </div>
            <RailFooter />
          </div>
        </div>
      )}

      {/* Main column */}
      <main className="lg:pl-64">
        <div key={location.pathname} className="fade-in mx-auto w-full max-w-5xl px-4 py-8 sm:px-8 sm:py-12">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
